// Portada de cada evento: texto, fotos y su ambiente animado (niebla y murciélagos, confeti, pasaporte con sellos,
// lámpara y globos, o nieve). Las fotos salen de los datos del evento; aquí solo va su acomodo, en % de su caja, en
// celular y en escritorio. Solo se dibujan las fotos que tocan en la pantalla, así no se descargan las que no se ven.
import React, { useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ESCRITORIO, fotosVisibles, RUTA_EVENTOS, waEvento } from '../../services/eventos';
import useEnVista from '../../hooks/useEnVista';
import useMedia from '../../hooks/useMedia';
import { grande, scrollToId } from '../../utils';
import { azar, Calabaza, Confeti, Destellos, Enfasis, Estrellas, Globo, Guirnalda, Lampara, Murcielagos, Niebla, Nieve, NombreEvento, Pelota, Pieza, Polaroid, Pulsera } from './Piezas';

/** @typedef {import('../../data/eventos').Evento} Evento */
/** @typedef {import('../../data/eventos').AmbienteEvento} AmbienteEvento */
/** @typedef {import('../../data/eventos').FondoPortada} FondoPortada */
/** @typedef {import('./Piezas').Lugar} Lugar */

/**
 * Props de las capas animadas y de las fotos de una portada.
 * @typedef {Object} PropsPortada
 * @property {Evento} evento
 * @property {boolean} escritorio   true desde 900 px de ancho
 */

/**
 * Capas animadas y fotos de un tipo de portada.
 * @typedef {Object} TipoPortada
 * @property {(props: PropsPortada) => import('react').ReactElement} Ambiente        capas animadas sobre toda la portada
 * @property {(props: PropsPortada) => import('react').ReactElement | null} Visual    fotos junto al texto
 */

/**
 * Lugar de una polaroid de la portada y desfase de su flotado (s).
 * @typedef {Object} LugarFoto
 * @property {Lugar} lugar
 * @property {number} retraso
 */

/** @type {string[]} */
const FOCOS_HALLOWEEN = ['#9BE15D', '#F7931E', '#B98CF2'];
/** @type {string[]} */
const FOCOS_NAVIDAD = ['#E0293B', '#2BA84A', '#F2C14E'];
/** @type {string[]} */
const COLORES_PIXAR = ['#E63946', '#FFC43D', '#2F6FDB', '#2BA84A', '#EF5DA8', '#16A3B8', '#8E5CD9', '#F77F00'];
/**
 * Animación de la palabra en cursiva del título, por ambiente: late en Oogie y se ilumina con la lámpara en Pixar.
 * @type {Partial<Record<AmbienteEvento, string>>}
 */
const CLASE_CURSIVA = { 'halloween-villanos': 'ev-late', pixar: 'ev-encendida' };

/**
 * Foto a media portada con corte en diagonal (solo en escritorio): es lo primero que se ve, así que se pide primero.
 * @param {{ foto: FondoPortada }} props
 */
const FotoDiagonal = ({ foto }) => (
  <div className="ev-diagonal" aria-hidden="true">
    <img src={grande(foto.src)} alt="" loading="eager" fetchpriority="high" decoding="async" style={{ objectPosition: foto.encuadre }} />
  </div>
);

/**
 * Las polaroids de la portada que tocan en esta pantalla, cada una en su lugar (por su orden en los datos). Ninguna
 * espera a acercarse a la pantalla y la primera se pide con prioridad alta.
 * @param {{ evento: Evento, escritorio: boolean, lugares: LugarFoto[] }} props
 */
function FotosPortada({ evento, escritorio, lugares }) {
  const visibles = fotosVisibles(evento.fotosPortada, escritorio);
  return (
    <>
      {evento.fotosPortada.map((f, i) => (visibles.includes(f)
        ? <Pieza key={f.src} lugar={lugares[i].lugar} flota retraso={lugares[i].retraso}><Polaroid foto={f} carga={f === visibles[0] ? 'primero' : 'visible'} /></Pieza>
        : null))}
    </>
  );
}

// ---------- Oogie Boogie Bash: luna, focos de feria, niebla verde y murciélagos ----------
function AmbienteVillanos() {
  return (
    <>
      <span className="ev-luna ev-solo-escritorio" aria-hidden="true" />
      <Estrellas n={30} semilla={11} color="#E9E1FF" hasta={60} />
      <Guirnalda colores={FOCOS_HALLOWEEN} n={20} caida={36} className="ev-guirnalda--portada" />
      <Niebla n={6} semilla={13} />
      <Murcielagos n={5} semilla={14} />
    </>
  );
}
/** @type {LugarFoto[]} */
const LUGARES_VILLANOS = [
  { lugar: { d: [0, 0, 48, -6], m: [2, 2.2, 42.9, -6] }, retraso: -1 },
  { lugar: { d: [42, 58, 56, 5], m: [45.5, 27.4, 52, 5] }, retraso: -3.5 }
];
/**
 * En el celular la luna va detrás de las fotos, donde no tapa el texto.
 * @param {PropsPortada} props
 */
const VisualVillanos = ({ evento, escritorio }) => (
  <div className="ev-visual">
    <span className="ev-luna ev-luna--movil ev-solo-movil" aria-hidden="true" />
    <FotosPortada evento={evento} escritorio={escritorio} lugares={LUGARES_VILLANOS} />
  </div>
);

// ---------- Not-So-Scary: confeti de Halloween, calabaza que se mece y la foto del confeti ----------
/** @param {PropsPortada} props */
function AmbienteConfeti({ evento, escritorio }) {
  return (
    <>
      <Estrellas n={24} semilla={21} color="#FFE9C7" hasta={70} className="ev-solo-izquierda" />
      {escritorio && evento.fondoPortada && <FotoDiagonal foto={evento.fondoPortada} />}
      <Pulsera evento={evento} className="ev-pulsera--suelta ev-flota ev-solo-escritorio" style={{ '--giro': '-7deg', right: '7%', bottom: '9%', animationDelay: '-2s' }} />
      <Confeti n={28} semilla={22} />
      <div className="ev-calabaza-colgada ev-pendulo" aria-hidden="true"><span className="ev-hilo" /><Calabaza tam={86} /></div>
    </>
  );
}
/** @type {LugarFoto[]} */
const LUGARES_CONFETI = [{ lugar: { d: [0, 0, 100, 0], m: [5.7, 2.2, 88.6, -3] }, retraso: -2 }];
/**
 * En escritorio la foto es la de la diagonal: aquí no va nada.
 * @param {PropsPortada} props
 */
const VisualConfeti = ({ evento, escritorio }) => (escritorio ? null : (
  <div className="ev-visual">
    <FotosPortada evento={evento} escritorio={escritorio} lugares={LUGARES_CONFETI} />
  </div>
));

// ---------- Food & Wine: pasaporte con sellos que aparecen e íconos de comida ----------
/**
 * Ícono de comida que flota: [clases de Font Awesome, x %, y %, tamaño px, color, giro°].
 * @typedef {[string, number, number, number, string, number]} IconoComida
 */

/** En escritorio, alrededor de la portada (en % de la portada). @type {IconoComida[]} */
const COMIDA = [
  ['fa-solid fa-wine-glass', 4.6, 84, 40, '#B8502A', 7],
  ['fa-solid fa-apple-whole', 44.4, 14.4, 38, '#3F5E3A', -8],
  ['fa-solid fa-cheese', 48, 87.8, 40, '#B8502A', 5],
  ['fa-solid fa-leaf', 95, 15.4, 34, '#3F5E3A', 10],
  ['fa-solid fa-utensils', 95.3, 87.3, 38, '#7B2335', -6],
  ['fa-solid fa-wine-bottle', 2.8, 18.3, 34, '#7B2335', 7]
];
/** En el celular, en la franja libre sobre el pasaporte (en % de la caja de fotos), lejos del texto y los botones. @type {IconoComida[]} */
const COMIDA_MOVIL = [
  ['fa-solid fa-wine-glass', 4, -2, 26, '#B8502A', 7],
  ['fa-solid fa-apple-whole', 22, -3, 24, '#3F5E3A', -8],
  ['fa-solid fa-cheese', 40, -1, 26, '#B8502A', 5]
];

/**
 * Íconos de comida que flotan.
 * @param {{ lista: IconoComida[], className?: string }} props
 */
const IconosComida = ({ lista, className = '' }) => (
  <>
    {lista.map(([icono, x, y, t, color, g], i) => (
      <i key={icono} className={`${icono} ev-comida ev-flota ${className}`} aria-hidden="true" style={{ left: `${x}%`, top: `${y}%`, fontSize: t, color, '--giro': `${g}deg`, animationDelay: `-${i * 1.3}s` }} />
    ))}
  </>
);
function AmbientePasaporte() {
  return <div className="ev-capa ev-solo-escritorio" aria-hidden="true"><IconosComida lista={COMIDA} /></div>;
}
/**
 * Sellos del pasaporte: [país, color, x %, y % dentro de su página, giro°, segundo en que se sella, redondo, página].
 * Se sellan uno por uno al abrir la página y se quedan.
 * @type {Array<[string, string, number, number, number, number, boolean, number]>}
 */
const SELLOS = [
  ['México', '#7B2335', 6.7, 33, -8, 0.3, true, 0],
  ['Italia', '#3F5E3A', 43.7, 59.4, 6, 0.8, false, 0],
  ['Japón', '#B8502A', 7.4, 8.3, 7, 1.3, true, 1],
  ['Francia', '#1F3A5F', 47.4, 14.4, -5, 1.8, false, 1],
  ['Canadá', '#9A7424', 14.8, 41.7, -10, 2.3, true, 1],
  ['Marruecos', '#7B2335', 51.9, 47.2, 9, 2.8, false, 1]
];
function Pasaporte() {
  /** @param {number} pagina  0 = izquierda, 1 = derecha */
  const sellos = (pagina) => SELLOS.filter((s) => s[7] === pagina).map(([pais, color, x, y, g, d, redondo]) => (
    <span key={pais} className={`ev-sello ev-sellar${redondo ? ' ev-sello--redondo' : ''}`} style={{ left: `${x}%`, top: `${y}%`, color, '--giro': `${g}deg`, animationDelay: `${d}s` }}>
      {pais}<em>EPCOT</em>
    </span>
  ));
  return (
    <div className="ev-pasaporte" aria-hidden="true">
      <div className="ev-pasaporte__pagina">
        <span className="ev-pasaporte__marca">Pasaporte del festival</span>
        <span className="ev-pasaporte__titulo">Food &amp; Wine <em>2026</em></span>
        <span className="ev-pasaporte__linea" />
        {sellos(0)}
      </div>
      <div className="ev-pasaporte__pagina">{sellos(1)}</div>
    </div>
  );
}
/** @type {LugarFoto[]} */
const LUGARES_PASAPORTE = [{ lugar: { d: [58, 26.9, 40.4, 7], m: [58.9, 8.1, 40, 7] }, retraso: -2 }];
/** @param {PropsPortada} props */
const VisualPasaporte = ({ evento, escritorio }) => (
  <div className="ev-visual">
    <Pieza lugar={{ d: [0, 8.4, 87, -4], m: [0, 13, 89.4, -4] }}><Pasaporte /></Pieza>
    <FotosPortada evento={evento} escritorio={escritorio} lugares={LUGARES_PASAPORTE} />
    <IconosComida lista={COMIDA_MOVIL} className="ev-solo-movil" />
  </div>
);

// ---------- Pixar Fest: bloques de color como su letrero, globos, pelota y la lámpara del título ----------
/**
 * Bloques de color repartidos en su caja.
 * @param {{ n: number, semilla: number, chicos: boolean, className: string }} props
 */
function Bloques({ n, semilla, chicos, className }) {
  const bloques = useMemo(() => {
    const r = azar(semilla);
    return Array.from({ length: n }, (_, i) => {
      const w = chicos ? 26 + r() * 38 : 50 + r() * 80;
      const h = chicos ? 40 + r() * 80 : 90 + r() * 170;
      return { x: r() * 92, y: r() * 80, w: Math.round(w), h: Math.round(h), color: COLORES_PIXAR[i % COLORES_PIXAR.length], d: r() * 5 };
    });
  }, [n, semilla, chicos]);
  return (
    <div className={`ev-bloques ${className}`} aria-hidden="true">
      {bloques.map((b, i) => <span key={i} className="ev-bloque ev-suave" style={{ left: `${b.x}%`, top: `${b.y}%`, width: b.w, height: b.h, background: b.color, animationDelay: `-${b.d.toFixed(1)}s` }} />)}
    </div>
  );
}
/** Globos: [color, x %, y %, tamaño] en escritorio (sobre la portada) y en celular (sobre las fotos). @type {Array<[string, number, number, number]>} */
const GLOBOS = [['#E63946', 89.6, 13.4, 40], ['#FFC43D', 92.8, 18.3, 46], ['#2F6FDB', 95.8, 12.7, 36], ['#EF5DA8', 91, 28, 34], ['#2BA84A', 96.7, 30.5, 30]];
/** @type {Array<[string, number, number, number]>} */
const GLOBOS_MOVIL = [['#E63946', 75, -6, 22], ['#FFC43D', 85, 0, 26], ['#2F6FDB', 94, -8, 20]];
function AmbientePixar() {
  return (
    <>
      <Bloques n={24} semilla={31} chicos={false} className="ev-bloques--portada ev-solo-escritorio" />
      <div className="ev-capa ev-capa--frente ev-solo-escritorio" aria-hidden="true">
        {GLOBOS.map(([color, x, y, t], i) => <span key={color} className="ev-globo ev-flotar-globo" style={{ left: `${x}%`, top: `${y}%`, animationDelay: `-${i * 1.1}s` }}><Globo color={color} tam={t} /></span>)}
        <span className="ev-pelota ev-rodar"><Pelota tam={54} /></span>
      </div>
    </>
  );
}
/** La segunda foto (Russell y Dug) es solo de escritorio. @type {LugarFoto[]} */
const LUGARES_PIXAR = [
  { lugar: { d: [3, 4, 60, -4], m: [8, 3, 46, -4] }, retraso: -1.5 },
  { lugar: { d: [59, 45, 39, 6], m: [0, 0, 0, 0] }, retraso: -3.2 }
];
/** @param {PropsPortada} props */
const VisualPixar = ({ evento, escritorio }) => (
  <div className="ev-visual">
    <Bloques n={14} semilla={32} chicos className="ev-solo-movil" />
    <FotosPortada evento={evento} escritorio={escritorio} lugares={LUGARES_PIXAR} />
    <div className="ev-solo-movil" aria-hidden="true">
      {GLOBOS_MOVIL.map(([color, x, y, t], i) => <span key={color} className="ev-globo ev-flotar-globo" style={{ left: `${x}%`, top: `${y}%`, animationDelay: `-${i * 1.1}s` }}><Globo color={color} tam={t} /></span>)}
    </div>
  </div>
);

// ---------- Very Merry: nieve, focos navideños, destellos y el castillo iluminado ----------
/** Destellos: [x %, y %, tamaño px]. @type {Array<[number, number, number]>} */
const DESTELLOS = [[6.4, 26.1, 22], [44.4, 32, 16], [41, 74.6, 20], [82, 17, 18], [92.4, 51.2, 14], [61.1, 84.1, 16]];
/** @type {Array<[number, number, number]>} */
const DESTELLOS_MOVIL = [[77, 19.3, 18], [10.3, 50, 14], [87.2, 43.2, 12]];
/** @param {PropsPortada} props */
function AmbienteNavidad({ evento, escritorio }) {
  return (
    <>
      <Estrellas n={24} semilla={41} color="#DCE7FF" hasta={80} className="ev-solo-izquierda" />
      {escritorio && evento.fondoPortada && <FotoDiagonal foto={evento.fondoPortada} />}
      <Pulsera evento={evento} className="ev-pulsera--suelta ev-flota ev-solo-escritorio" style={{ '--giro': '6deg', right: '7%', bottom: '9%', animationDelay: '-1.5s' }} />
      <Guirnalda colores={FOCOS_NAVIDAD} n={22} caida={32} className="ev-guirnalda--portada" />
      <Nieve n={44} semilla={43} />
      <Destellos puntos={DESTELLOS} semilla={44} className="ev-solo-escritorio" />
      <Destellos puntos={DESTELLOS_MOVIL} semilla={45} className="ev-solo-movil" />
    </>
  );
}
/** @type {LugarFoto[]} */
const LUGARES_NAVIDAD = [
  { lugar: { d: [0, 0, 50, 0], m: [4, 3, 46.9, -5] }, retraso: -1 },
  { lugar: { d: [50, 0, 50, 0], m: [52.6, 9.6, 42.9, 6] }, retraso: -3 }
];
/**
 * En escritorio la foto es la de la diagonal: aquí no va nada.
 * @param {PropsPortada} props
 */
const VisualNavidad = ({ evento, escritorio }) => (escritorio ? null : (
  <div className="ev-visual">
    <FotosPortada evento={evento} escritorio={escritorio} lugares={LUGARES_NAVIDAD} />
  </div>
));

/** Ambiente (capas animadas) y fotos de cada tipo de portada. @type {Record<AmbienteEvento, TipoPortada>} */
const AMBIENTES = {
  'halloween-villanos': { Ambiente: AmbienteVillanos, Visual: VisualVillanos },
  'halloween-confeti': { Ambiente: AmbienteConfeti, Visual: VisualConfeti },
  pasaporte: { Ambiente: AmbientePasaporte, Visual: VisualPasaporte },
  pixar: { Ambiente: AmbientePixar, Visual: VisualPixar },
  navidad: { Ambiente: AmbienteNavidad, Visual: VisualNavidad }
};

/**
 * Portada de la página de un evento.
 * @param {{ evento: Evento }} props
 */
export default function Portada({ evento }) {
  const ref = useRef(null);
  const visible = useEnVista(ref);
  const escritorio = useMedia(ESCRITORIO);
  const t = evento.tema;
  const { Ambiente, Visual } = AMBIENTES[evento.ambiente];
  const estilo = {
    '--ev-fondo': t.fondo, '--ev-texto': t.texto, '--ev-suave': t.suave, '--ev-acento': t.acento, '--ev-sub': t.subtitulo,
    '--ev-chip': t.oscuro ? 'rgba(255, 255, 255, .1)' : t.claro, '--ev-chip-texto': t.oscuro ? t.acento : t.oscuroAcento
  };
  const titulo = <Enfasis texto={evento.titulo} claseEm={CLASE_CURSIVA[evento.ambiente] || ''} />;
  return (
    <section ref={ref} className={`ev-portada ev-portada--${evento.ambiente} ev-anim${t.oscuro ? ' ev-portada--noche' : ''}${visible ? '' : ' ev-pausa'}`} style={estilo}>
      <Ambiente evento={evento} escritorio={escritorio} />
      <div className="wrap ev-portada__in">
        <div className="ev-portada__txt">
          <nav aria-label="Ruta" className="ev-ruta"><Link to={RUTA_EVENTOS}>Eventos Disney</Link><span aria-hidden="true">›</span><span><NombreEvento evento={evento} /></span></nav>
          <span className="ev-chip"><i className="fa-solid fa-location-dot" />{evento.parque} · {evento.anio}</span>
          <h1 className="ev-portada__h1">
            {evento.ambiente === 'pixar'
              ? <span className="ev-titulo-lampara">{titulo}<span className="ev-lampara" aria-hidden="true"><Lampara /></span></span>
              : titulo}
          </h1>
          <p className="ev-portada__sub">{evento.sub}</p>
          <p className="ev-portada__lead">{evento.lead}</p>
        </div>
        <div className="ev-portada__visual"><Visual evento={evento} escritorio={escritorio} /></div>
        <div className="ev-portada__btns">
          <a className="btn-wa" href={waEvento(evento)} target="_blank" rel="noopener noreferrer"><i className="fa-brands fa-whatsapp" />Quiero vivirla</a>
          <button type="button" className="ev-btn-borde" onClick={() => scrollToId('vivi')}>Ver mis fotos</button>
        </div>
      </div>
    </section>
  );
}
